import { useAuth0 } from "@auth0/auth0-react";
import Dialog from "@material-ui/core/Dialog";
import { IconButton, TableContainer } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import AddBoxOutlinedIcon from "@material-ui/icons/AddBoxOutlined";
import AllInclusiveIcon from "@material-ui/icons/AllInclusive";
import IndeterminateCheckBoxOutlinedIcon from "@material-ui/icons/IndeterminateCheckBoxOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNewOutlined";
import { ReactComponent as InstallmentIcon } from "assets/icons/recurring-icon.svg";
import Pagination from "@material-ui/lab/Pagination";
import Paper from "@mui/material/Paper";
import sortImg from "assets/icons/sort-able-svg.svg";
import { BsSortDown, BsSortUp } from "react-icons/bs";
import Action from "components/ActionPopOver";
import AlertDialog from "components/Dialog/index";
import { MyCheckbox } from "components";
import RightDrawer from "components/drawer";
import ExportOptions from "components/ExportOptions/ExportOptions";
import Loader from "components/loader/Loader";
import Spinner from "components/spinner";
import MyModal from "components/MyModal";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastr } from "react-redux-toastr";
import FinanceService from "../../../Lib/API/Finance";
import FinanceDrawer from "../Drawer";
import ApplyLink from "../TransationTableComponents/ApplyLink";
import UpdatePaymentMethod from "components/UpdatePaymentMethod/index";
import NewDialog from "components/NewDialog/Dialog";
import { numberFormat } from "helper/numberFormat";
import { formatDate } from "helper/dateFormat";
import ShareDocumentsModal from "../Documents/ShareDocumentsModal/index";
import ViewDocumentModal from "../Documents/ViewDocumentModal/index";
import * as XLSX from "xlsx";
import { sanitizeRows } from "utils/exportSanitize";
import { fontConfig } from "theme/fontConfig";

const getChargeTypeLabel = (type) => {
  switch (type) {
    case "installment": return "CHARGE";
    case "recurringInternal": return "CHARGE";
    case "recurring": return "CHARGE";
    case "recurringExternal": return "DONATION";
    default: return (type || "").replace(/([A-Z])/g, " $1").trim().toUpperCase();
  }
};

const getChargeTypeColor = (type) => type === "recurringExternal" ? "#81DC91" : "#FBA12C";

const getPaymentStatus = (row) => {
  if (row.status === "disable" || row.status === "completed") return "Closed";
  if (row.paid >= row.amount && row.amount > 0) return "Closed";
  if (row.paid > 0 && row.paid < row.amount) return "Partially Paid";
  return "Open";
};

const getInstallmentSubStatus = (status) => {
  switch (status) {
    case "active": return "Installments Active";
    case "disable": return "Installments Disabled";
    case "on-hold": return "Installments On-hold";
    case "completed": return "Installments Completed";
    default: return "";
  }
};

const getRecurringSubStatus = (status) => {
  switch (status) {
    case "active": return "Recurring Active";
    case "on-hold": return "Recurring On-hold";
    case "disable": return "Recurring Disabled";
    case "completed": return "Recurring Completed";
    default: return "";
  }
};

const StatusCell = ({ row }) => {
  return (
    <div className="transaction-status">
      <div style={{ width: 22 }} />
      <div className="pl-0">
        <div className="transaction-account-name">
          {row.typeName ?? "-"}
        </div>
        <div className="sub-status text-capitalize">
          {row.status ?? ""}
        </div>
      </div>
    </div>
  );
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "-";
  return formatDate({ date: dateStr, formatType: "default" }) || "-";
};

const formatAmount = (amount) => {
  if (amount == null) return "-";
  return numberFormat({ number: amount, decimal: 2, style: "currency" });
};

const COLUMNS = [
  { label: "STATUS", field: "status", minWidth: 140 },
  { label: "ACCOUNT NAME", field: "accountName", minWidth: 160 },
  { label: "FREQUENCY", field: "frequency", minWidth: 90 },
  { label: "CHARGED", field: "amount", minWidth: 90 },
  { label: "PAID", field: "paid", minWidth: 80 },
  { label: "DUE", field: "totalDue", minWidth: 80, noSort: true },
  { label: "CYCLES", field: "cycles", minWidth: 70, noSort: true },
  { label: "CREATED DATE", field: "createdAt", minWidth: 120 },
  { label: "NEXT PAYMENT DATE", field: "futurePaymentDate", minWidth: 160, noSort: true },
  { label: "NEXT PAYMENT AMOUNT", field: "futurePaymentAmount", minWidth: 180, noSort: true },
];

const recurringStatusColor = {
  active: "#81DC91",
  "on-hold": "#2555ef21",
  declined: "#fd02027d",
  disable: "#fd02027d",
};

const isRecurringType = (type) =>
  type === "recurring" || type === "recurringExternal" || type === "recurringInternal";

const isInstallmentType = (type) => type === "installment";

// Map payment plan row to the shape FinancePopover expects
const mapToFinanceRow = (row) => {
  const recurring = isRecurringType(row.type);
  const installment = isInstallmentType(row.type);
  // Most recent payment has the real charge metadata
  const payments = row.payments ?? [];
  const lastPayment = payments[payments.length - 1] ?? payments[0];
  const hasPaid = row.paid > 0 && payments.length > 0;
  return {
    _id: row._id,
    isRecurring: recurring,
    charge_type: row.charge_type,
    total_amount: row.amount,
    total_paid: row.paid,
    Ledger: row.Ledger,
    SubLedger: row.SubLedger,
    comment_invoice: row.comment_invoice,
    charge: {
      _id: row.chargeId ?? payments[0]?.chargeId ?? row._id,
      amount: row.amount,
      paid: row.paid,
      due_date: row.due_date ?? lastPayment?.date ?? row.startDate,
      // if there are payments, charge status is "payment"; otherwise use plan status
      status: hasPaid ? "payment" : row.status,
      discount: row.discount ?? 0,
      is_synced: row.is_synced,
      synced_Batch: row.synced_Batch,
      isInstallment: installment,
      isRecurring: recurring,
      sub_status: null,
      payment: {
        status: row.status === "declined" ? "declined" : "active",
        convenient_fee: lastPayment?.convenienceFee ?? lastPayment?.convenient_fee ?? row.convenient_fee,
      },
      installmentDetails: installment ? {
        amount: row.amount,
        cycle: row.cycles,
        paid: row.paid,
        installmentStart: row.createdAt,
        status: row.status,
        installmentId: row._id,
      } : undefined,
    },
    recurringDetails: recurring ? {
      recurringStatus: row.status,
      recurringType: row.type,
      hasProvider: true,
    } : undefined,
    Account: row.Account,
  };
};

const ExpandablePaymentRow = ({ row, i, checked, onCheckboxChange, onRefresh }) => {
  const { getAccessTokenSilently } = useAuth0();
  const orgKey = (() => {
    try { return JSON.parse(localStorage.getItem("organization"))?.orgkey; } catch { return ""; }
  })();

  const [open, setOpen] = useState(false);
  const [recurringAction, setRecurringAction] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelInstallConfirm, setCancelInstallConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteReversedConfirm, setDeleteReversedConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sharePaymentOpen, setSharePaymentOpen] = useState(false);
  const [copyPaymentLink, setCopyPaymentLink] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState({});
  const [openApplyModal, setOpenApplyModal] = useState(false);
  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [viewDocumentContent, setViewDocumentContent] = useState(null);
  const [viewDocumentId, setViewDocumentId] = useState(null);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [documentRow, setDocumentRow] = useState(null);
  const [updatePaymentMethod, setUpdatePaymentMethod] = useState({ open: false, data: null });
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const confirmEditRef = React.useRef(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [fetchedTransactionId, setFetchedTransactionId] = useState(null);

  const hasPayments = row.payments?.length > 0;
  const mappedRow = mapToFinanceRow(row);
  const transactionId = (typeof row.transaction === "object" ? row.transaction?._id : row.transaction) ?? row._id;

  const openPaymentDrawer = async (p) => {
    const initial = {
      ...mappedRow,
      isExpandRow: true,
      charge: {
        ...mappedRow.charge,
        _id: p.chargeId,
        amount: p.amount,
        paid: p.amount,
        status: "payment",
        payment: {
          status: "active",
          convenient_fee: p.convenienceFee,
          History: { paymnetMethod: p.paymentMethod },
        },
      },
      transactionId: transactionId,
    };
    setDrawerData({});
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await FinanceService.getTransactionById(token, transactionId);
      const tx = res?.result ?? res;
      if (!tx) { setDrawerData(initial); return; }
      const charge = tx?.Charges?.find((c) => c._id === p.chargeId) ?? tx?.Charges?.[0] ?? tx?.charge;
      setDrawerData({
        ...tx,
        ...mappedRow,
        _id: tx._id ?? transactionId,
        isExpandRow: true,
        charge: {
          ...(charge ?? {}),
          _id: p.chargeId ?? charge?._id,
          amount: p.amount,
          paid: p.amount,
          status: "payment",
          discount: charge?.discount ?? 0,
          payment: {
            status: "active",
            convenient_fee: p.convenienceFee ?? charge?.payment?.convenient_fee,
            History: { paymnetMethod: p.paymentMethod },
          },
        },
        charge_type: mappedRow.charge_type,
        transactionId: tx._id ?? row._id,
        isRecurring: mappedRow.isRecurring,
        recurringDetails: mappedRow.recurringDetails,
        Ledger: tx.Ledger ?? mappedRow.Ledger,
        SubLedger: tx.SubLedger ?? mappedRow.SubLedger,
        comment_invoice: tx.comment_invoice ?? mappedRow.comment_invoice,
        total_amount: tx.total_amount ?? mappedRow.total_amount,
        total_paid: tx.total_paid ?? mappedRow.total_paid,
        is_splitted: tx.is_splitted,
        number_of_split: tx.number_of_split,
        discount: tx.discount,
        discount_type: tx.discount_type,
        tribute_info: tx.tribute_info,
      });
    } catch (_) {
      setDrawerData(initial);
    } finally {
      setDrawerLoading(false);
    }
  };

  const openMainDrawer = async () => {
    setDrawerData({});
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await FinanceService.getTransactionById(token, transactionId);
      const tx = res?.result ?? res;
      if (!tx) { setDrawerData({ ...mappedRow, transactionId }); return; }
      if (tx._id) setFetchedTransactionId(tx._id);
      const fetchedCharge = tx?.Charges?.[0] ?? tx?.charge ?? {};
      setDrawerData({
        ...tx,
        ...mappedRow,
        _id: tx._id ?? transactionId,
        charge: {
          // start with mappedRow.charge (correct amount, paid, status)
          ...mappedRow.charge,
          // enrich metadata fields from fetched charge
          _id: fetchedCharge?._id ?? mappedRow.charge._id,
          due_date: fetchedCharge?.due_date ?? mappedRow.charge.due_date,
          is_synced: fetchedCharge?.is_synced ?? mappedRow.charge.is_synced,
          synced_Batch: fetchedCharge?.synced_Batch ?? mappedRow.charge.synced_Batch,
          // use fetched charge amounts for correct totals (installment total, not per-payment)
          amount: fetchedCharge?.amount ?? mappedRow.charge.amount,
          paid: fetchedCharge?.paid ?? mappedRow.charge.paid,
          discount: fetchedCharge?.discount ?? mappedRow.charge.discount,
          installmentDetails: fetchedCharge?.installmentDetails ?? mappedRow.charge.installmentDetails,
          payment: {
            ...mappedRow.charge.payment,
            convenient_fee: fetchedCharge?.payment?.convenient_fee ?? mappedRow.charge.payment?.convenient_fee,
          },
        },
        charge_type: mappedRow.charge_type,
        transactionId: tx._id ?? row._id,
        isRecurring: mappedRow.isRecurring,
        recurringDetails: mappedRow.recurringDetails,
        Ledger: tx.Ledger ?? mappedRow.Ledger,
        SubLedger: tx.SubLedger ?? mappedRow.SubLedger,
        comment_invoice: tx.comment_invoice ?? mappedRow.comment_invoice,
        total_amount: tx.total_amount ?? mappedRow.total_amount,
        total_paid: tx.total_paid ?? mappedRow.total_paid,
        is_splitted: tx.is_splitted,
        number_of_split: tx.number_of_split,
        discount: tx.discount,
        discount_type: tx.discount_type,
        tribute_info: tx.tribute_info,
      });
    } catch (_) {
      setDrawerData({ ...mappedRow, transactionId });
    } finally {
      setDrawerLoading(false);
    }
  };

  useEffect(() => {
    if (!recurringAction) return;
    if (recurringAction === "disable") {
      setCancelConfirm(true);
      return;
    }
    handleRecurringAction(recurringAction);
    setRecurringAction("");
  }, [recurringAction]);

  const handleRecurringAction = async (status) => {
    setActionLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await FinanceService.PaymentInfoAction({ transactionId, status }, token);
      toastr.success("Success", "Action completed successfully");
      onRefresh();
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRecurring = async () => {
    await handleRecurringAction("disable");
    setCancelConfirm(false);
    setRecurringAction("");
  };

  const handleCancelInstallment = async () => {
    setActionLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await FinanceService.cancelInstallment(token, {
        installmentId: row._id,
        chargeId: mappedRow.charge?._id ?? row.payments?.[0]?.chargeId,
      });
      toastr.success("Success", "Installment cancelled successfully");
      onRefresh();
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
      setCancelInstallConfirm(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await FinanceService.deleteTransaction({ _id: transactionId }, token, mappedRow.charge?._id ?? "");
      toastr.success("Success", "Deleted successfully");
      onRefresh();
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
      setDeleteConfirm(false);
      setDeleteReversedConfirm(false);
    }
  };

  const handleEdit = () => {
    if (!confirmEditRef.current && isInstallmentType(row.type) && row.status === "active") {
      confirmEditRef.current = false;
      setConfirmEditOpen(true);
      return;
    }
    confirmEditRef.current = false;
    document.dispatchEvent(new CustomEvent("edit-charge", { detail: mappedRow }));
  };

  const handlePayNow = () => {
    document.dispatchEvent(new CustomEvent("pay-now", { detail: mappedRow }));
  };

  const handleSharePaymentLink = () => {
    const mainUrl = window.location.origin;
    const accountId = row.Account?.id ?? row.Account?._id;
    const link =
      mainUrl === "https://admin.eztmpl.dev" || mainUrl === "http://localhost:3000"
        ? `https://shalom.eztmpl.dev/charges?accountId=${accountId}`
        : mainUrl === "https://admin.eztmpl.com"
        ? `https://demo.eztmpl.com/charges?accountId=${accountId}`
        : mainUrl === "https://admin.easyshul.com"
        ? `https://${orgKey}.easyshul.com/charges?accountId=${accountId}`
        : "";
    setCopyPaymentLink(link);
    setSharePaymentOpen(true);
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }} className={open ? "exp_head" : ""}>
        <TableCell padding="checkbox" className="main-check main-width-class">
          <MyCheckbox name="visible" handlechange={(e) => onCheckboxChange(e, row)} checked={checked} />
        </TableCell>
        <TableCell className="expand-button main-width-class">
          {hasPayments && (
            <IconButton aria-label="expand row" size="large" onClick={() => setOpen((o) => !o)} className="expandale-icon">
              {open ? <IndeterminateCheckBoxOutlinedIcon /> : <AddBoxOutlinedIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell className="main-width-class" width="5%" component="th" scope="row" onClick={openMainDrawer} style={{ cursor: "pointer" }}>
          {isRecurringType(row.type) ? (
            <div
              className="recurring_indicator"
              style={{ backgroundColor: recurringStatusColor[row.status] || "#2555ef21" }}
            >
              <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 0C2.02085 0 0 2.02085 0 4.5C0 6.9795 2.02085 9 4.5 9C6.76904 9 8.65385 7.308 8.95673 5.1165C8.96954 5.04742 8.96819 4.97646 8.95277 4.90791C8.93736 4.83937 8.90819 4.77466 8.86703 4.71772C8.82588 4.66078 8.7736 4.61278 8.71336 4.57663C8.65311 4.54048 8.58616 4.51693 8.51655 4.50741C8.44694 4.4979 8.37612 4.5026 8.30838 4.52125C8.24064 4.5399 8.1774 4.5721 8.12247 4.6159C8.06754 4.65971 8.02207 4.71421 7.98881 4.7761C7.95556 4.83798 7.93521 4.90598 7.929 4.97596C7.69604 6.66173 6.25535 7.96154 4.5 7.96154C2.58231 7.96154 1.03846 6.41804 1.03846 4.5C1.03846 2.58231 2.58231 1.03846 4.5 1.03846C5.62015 1.03846 6.61465 1.56981 7.24777 2.39054L6.63092 3.00738C6.579 3.05931 6.5655 3.14273 6.58765 3.21265C6.60842 3.28258 6.66692 3.32758 6.73927 3.34246C7.67285 3.52592 8.74938 3.44492 8.79438 3.43973C8.84 3.43151 8.88176 3.40881 8.91346 3.375C8.94462 3.34454 8.97231 3.31269 8.97854 3.26665C8.98373 3.22165 9.06508 2.13577 8.88092 1.20081C8.87544 1.166 8.8604 1.13339 8.83748 1.10663C8.81456 1.07986 8.78466 1.05998 8.75112 1.04919C8.6805 1.02773 8.59742 1.04054 8.54585 1.09246L7.98335 1.65496C7.15777 0.647308 5.90088 0 4.5 0Z" fill="#3C4467" />
              </svg>
            </div>
          ) : null}
        </TableCell>
        <TableCell style={{ cursor: "pointer" }} onClick={openMainDrawer}><StatusCell row={row} /></TableCell>
        <TableCell className="account-header main-width-class" align="left" style={{ minWidth: 160 }}>
          {row?.Account?.payment?.account_type === "visitor" || row?.Account?.payment?.account_type === "registration" ? (
            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "8px" }}>
              <div className="transaction-account-name visitor_style" onClick={openMainDrawer}>
                {row?.Account?.payment?.account_type === "visitor" ? "Visitor" : "Registration"}
              </div>
              <div className="apply_link" onClick={() => setOpenApplyModal(true)}>Apply</div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div className="transaction-account-name ellipsDueAmount" style={{ cursor: "pointer" }} onClick={openMainDrawer}>
                {row.Account?.name ?? "-"}
              </div>
              {(row.Account?.id ?? row.Account?._id) && (
                <a
                  href={`/member-details/${row.Account?.id ?? row.Account?._id}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: "flex", alignItems: "center", color: "#2555EF", flexShrink: 0 }}
                >
                  <OpenInNewIcon style={{ fontSize: 10, width: 18, height: 18 }} />
                </a>
              )}
            </div>
          )}
        </TableCell>
        <TableCell style={{ fontSize: 13, textTransform: "capitalize", cursor: "pointer" }} onClick={openMainDrawer}>{row.frequency ?? "-"}</TableCell>
        <TableCell style={{ fontSize: 13, cursor: "pointer" }} onClick={openMainDrawer}>{row.totalAmount == null ? "-" : formatAmount(row.totalAmount)}</TableCell>
        <TableCell style={{ fontSize: 13, cursor: "pointer" }} onClick={openMainDrawer}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {formatAmount(row.paid)}
            {isInstallmentType(row.type) && <InstallmentIcon className="ml-2" />}
          </div>
        </TableCell>
        <TableCell style={{ fontSize: 13, cursor: "pointer" }} onClick={openMainDrawer}>{row.dueAmount == null ? "-" : formatAmount(row.dueAmount)}</TableCell>
        <TableCell style={{ fontSize: 13, cursor: "pointer", paddingLeft: 16 }} onClick={openMainDrawer}>
          {row.cycleType === "forever" ? <AllInclusiveIcon style={{ width: 16, height: 16, verticalAlign: "middle", color: "inherit" }} /> : (row.cycles ?? "-")}
        </TableCell>
        <TableCell style={{ fontSize: 13, cursor: "pointer" }} onClick={openMainDrawer}>{fmtDate(row.createdAt)}</TableCell>
        <TableCell style={{ fontSize: 13, cursor: "pointer" }} onClick={openMainDrawer}>{row.nextDate ? fmtDate(row.nextDate) : "-"}</TableCell>
        <TableCell style={{ fontSize: 13, cursor: "pointer" }} onClick={openMainDrawer}>{row.nextPayment != null ? formatAmount(row.nextPayment) : "-"}</TableCell>
        <TableCell align="right" className="exp-action-head main-width-class sticky-body-column">
          {(() => {
            const isRecurring = isRecurringType(row.type);
            const isInstallment = isInstallmentType(row.type);
            const isDeclined = mappedRow.charge?.payment?.status === "declined";
            const isActive = row.status === "active";
            const isCompleted = row.status === "completed";
            const hasActions =
              (isInstallment && isActive) ||              // cancel payment + update payment method
              (isRecurring && !isCompleted) ||            // on-hold/activate, cancel
              (isRecurring && isActive) ||                // update payment method
              (isRecurring && isDeclined);                // update payment info
            return (
              <Action
                style={{ width: "16px" }}
                action="finance"
                reversalCheck={false}
                row={mappedRow}
                checkData={[]}
                onPayClick={undefined}
                isEditHide={true}
                disableActionButton={!hasActions}
                onSharePymentClick={undefined}
                onUpdatePaymentClick={isRecurring && isDeclined ? () => setUpdatePaymentMethod({ open: true, data: { ...mappedRow, transactionId } }) : undefined}
                enableUpdatePaymentMethod={(isRecurring && isActive) || (isInstallment && isActive)}
                onUpdatePaymentMethod={() => setUpdatePaymentMethod({ open: true, data: { ...mappedRow, transactionId, isInstallmentPlan: isInstallment } })}
                OnRecurringAction={isRecurring ? setRecurringAction : undefined}
                onCancelClick={isRecurring ? () => setCancelConfirm(true) : undefined}
                onCancelPaymentClick={isInstallment && isActive ? () => setCancelInstallConfirm(true) : null}
              />
            );
          })()}
        </TableCell>
      </TableRow>

      {hasPayments && open && (
        <TableRow className="MuiTableRow-root-temp2">
          <TableCell className="border-0" style={{ padding: "4px 16px" }} />
          <TableCell className="border-0" style={{ padding: "4px 16px" }} />
          <TableCell className="border-0" style={{ padding: "4px 16px" }} />
          <TableCell className="border-0" style={{ padding: "4px 16px", whiteSpace: "nowrap" }}><span className="bulkCell_header" style={{ color: "#4d5565" }}>STATUS</span></TableCell>
          <TableCell className="border-0" style={{ padding: "4px 16px", whiteSpace: "nowrap" }}><span className="bulkCell_header" style={{ color: "#4d5565" }}>DATE</span></TableCell>
          <TableCell className="border-0" style={{ padding: "4px 16px", whiteSpace: "nowrap" }}><span className="bulkCell_header" style={{ color: "#4d5565" }}>AMOUNT</span></TableCell>
          <TableCell className="border-0" style={{ padding: "4px 16px", whiteSpace: "nowrap" }}><span className="bulkCell_header" style={{ color: "#4d5565" }}>PAYMENT METHOD</span></TableCell>
          <TableCell className="border-0" style={{ padding: "4px 16px", whiteSpace: "nowrap" }} colSpan={7}><span className="bulkCell_header" style={{ color: "#4d5565" }}>CHARGE ID</span></TableCell>
        </TableRow>
      )}
      {hasPayments && open && row.payments.map((p, idx) => (
        <TableRow key={p.chargeId ?? idx} className="child_exp_row" style={{ cursor: "pointer" }} onClick={() => openPaymentDrawer(p)}>
          <TableCell className="border-0" style={{ padding: "8px 16px" }} />
          <TableCell className="border-0" style={{ padding: "8px 16px" }} />
          <TableCell className="border-0" style={{ padding: "8px 16px" }} />
          <TableCell className="border-0" style={{ padding: "8px 16px" }}>
            <div className="status" style={{ color: "#81DC91", fontSize: 11, fontWeight: 600 }}>PAYMENT</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Paid</div>
          </TableCell>
          <TableCell className="border-0" style={{ padding: "8px 16px", fontSize: 13 }}>
            {fmtDate(p.date)}
          </TableCell>
          <TableCell className="border-0" style={{ padding: "8px 16px", fontSize: 13 }}>
            {formatAmount(p.amount)}
          </TableCell>
          <TableCell className="border-0" style={{ padding: "8px 16px", fontSize: 13, textTransform: "capitalize" }}>
            <div>{p.paymentMethod?.method ? p.paymentMethod.method.replace(/_/g, " ") : "-"}</div>
            <div style={{ fontSize: 11, color: "#acacac" }}>
              {p.paymentMethod?.last4 ? `···· ${p.paymentMethod.last4}` : ""}
              {p.paymentMethod?.brand ? ` ${p.paymentMethod.brand}` : ""}
            </div>
          </TableCell>
          <TableCell className="border-0" style={{ padding: "8px 16px", fontSize: 13 }} colSpan={7}>
            {p.chargeId ?? "-"}
          </TableCell>
        </TableRow>
      ))}

      <MyModal className="invite-modal" open={openApplyModal} onClose={() => setOpenApplyModal(false)}>
        <ApplyLink closeModal={() => setOpenApplyModal(false)} row={mappedRow} />
      </MyModal>

      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawerLoading ? <div style={{ width: 380, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}><Spinner /></div> : <FinanceDrawer
          data={drawerData}
          hideDelete={true}
          hidePayNow={true}
          hideSharePaymentLink={true}
          disableActionButton={(() => {
            const dr = drawerData;
            const drIsRecurring = isRecurringType(dr.type);
            const drIsInstallment = isInstallmentType(dr.type);
            const drIsActive = dr.status === "active";
            const drIsCompleted = dr.status === "completed";
            const drIsDeclined = dr.charge?.payment?.status === "declined";
            const drHasActions =
              (drIsInstallment && drIsActive) ||
              (drIsRecurring && !drIsCompleted) ||
              (drIsRecurring && drIsActive) ||
              (drIsRecurring && drIsDeclined);
            return !drHasActions;
          })()}
          handleClose={() => setDrawerOpen(false)}
          setsavetribute={() => {}}
          onReversalClick={() => {}}
          viewDocumentHandler={(documentId) => setViewDocumentId(documentId)}
          downloadFileHandler={async (docRow) => {
            const { _id, name, type } = docRow;
            const token = await getAccessTokenSilently();
            await FinanceService.downloadDocument(token, _id).then((response) => {
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement("a");
              link.href = url;
              const currentDate = formatDate({ date: new Date(), formatType: "defaultWithUTC" });
              const fileType = type === "tax_statement" ? "Statement" : type.charAt(0).toUpperCase() + type.slice(1);
              link.setAttribute("download", `${fileType}_${name.replaceAll(" ", "_")}_${currentDate}.pdf`);
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
            });
          }}
          documentHandler={(docRow) => { setDocumentRow(docRow); setOpenShareModal(true); }}
          onSharePaymentLinkClick={() => {}}
          viewLetterHandler={(content) => { setViewDocumentContent(content); setOpenDocumentModal(true); }}
        />}
      </RightDrawer>

      {confirmEditOpen && (
        <NewDialog
          isOpen={confirmEditOpen}
          handleClose={() => { confirmEditRef.current = false; setConfirmEditOpen(false); }}
          content={<p style={{ textTransform: "none" }}>This charge is currently associated with an active payment plan. Click 'Confirm' to cancel the existing payment plan and proceed with the editing.</p>}
        >
          <div className="payment-modal">
            <button className="payment-modal-btn" onClick={() => { confirmEditRef.current = false; setConfirmEditOpen(false); }}>Cancel</button>
            <button className="payment-modal-btn payment-modal-btn-primary" onClick={() => { confirmEditRef.current = true; setConfirmEditOpen(false); handleEdit(); }}>Confirm</button>
          </div>
        </NewDialog>
      )}

      <MyModal className="new-charge-modal" open={updatePaymentMethod.open} onClose={() => setUpdatePaymentMethod({ open: false, data: null })}>
        <UpdatePaymentMethod
          title="Update Payment Information"
          closeModal={() => setUpdatePaymentMethod({ open: false, data: null })}
          accountId={updatePaymentMethod?.data?.Account?.id ?? updatePaymentMethod?.data?.Account?._id}
          isInstallment={updatePaymentMethod?.data?.isInstallmentPlan || false}
          data={updatePaymentMethod?.data}
          reFetchData={onRefresh}
        />
      </MyModal>

      {(openDocumentModal || viewDocumentId !== null) && (
        <Dialog
          open={openDocumentModal || viewDocumentId !== null}
          onClose={() => { setOpenDocumentModal(false); setViewDocumentContent(null); setViewDocumentId(null); }}
          PaperProps={{ style: { width: "888px", height: "90vh", overflow: "hidden", borderRadius: "22px" } }}
          fullWidth
          maxWidth="md"
        >
          <ViewDocumentModal
            documentHtmlContent={viewDocumentContent ?? null}
            documentId={viewDocumentId ?? null}
            closeModal={() => { setViewDocumentContent(null); setOpenDocumentModal(false); setViewDocumentId(null); }}
          />
        </Dialog>
      )}

      <Dialog
        open={openShareModal}
        onClose={() => setOpenShareModal(false)}
        PaperProps={{ style: { width: "888px", height: "637px", overflowX: "hidden", borderRadius: "22px" } }}
        fullWidth
        maxWidth="md"
      >
        <ShareDocumentsModal
          closeModal={() => setOpenShareModal(false)}
          documentsIds={[documentRow?._id]}
          documentType={documentRow?.type}
          defaultEmail={documentRow?.account_details?.defaultEmails || []}
          type="Document"
        />
      </Dialog>

      <AlertDialog
        open={cancelConfirm}
        onDecline={() => { setCancelConfirm(false); setRecurringAction(""); }}
        onAccept={handleCancelRecurring}
        loading={actionLoading}
        title="Are you sure?"
        description="Do you want to cancel this recurring payment?"
      />

      <AlertDialog
        open={cancelInstallConfirm}
        onDecline={() => setCancelInstallConfirm(false)}
        onAccept={handleCancelInstallment}
        loading={actionLoading}
        title="Are you sure?"
        description="Do you want to cancel the instalment?"
      />

      <AlertDialog
        open={deleteConfirm}
        onDecline={() => setDeleteConfirm(false)}
        onAccept={handleDelete}
        loading={actionLoading}
        title="Are you sure?"
        description="Do you want to delete this payment plan?"
      />

      <AlertDialog
        open={deleteReversedConfirm}
        onDecline={() => setDeleteReversedConfirm(false)}
        onAccept={handleDelete}
        acceptBtnText="Delete"
        loading={actionLoading}
        title="Delete Charge"
        description={
          <div style={{ textAlign: "left", color: "#002E5C" }}>
            <p style={{ marginBottom: "10px", fontWeight: "700" }}>This charge is already paid, reversed, or synced.</p>
            <p style={{ marginBottom: "10px" }}>Deleting it here will not update your accounting records in your accounting system.</p>
            <p style={{ marginBottom: "20px" }}>You are responsible for making any necessary changes directly in your accounting system.</p>
            <p style={{ fontWeight: "700" }}>Are you sure you want to delete this charge?</p>
          </div>
        }
      />

      {sharePaymentOpen && (
        <Dialog
          open={sharePaymentOpen}
          onClose={() => setSharePaymentOpen(false)}
          PaperProps={{ style: { width: "888px", height: "637px", overflowX: "hidden", borderRadius: "22px" } }}
          fullWidth
          maxWidth="md"
        >
          <ShareDocumentsModal
            closeModal={() => setSharePaymentOpen(false)}
            type="sharePaymentLink"
            accountId={row.Account?.id ?? row.Account?._id}
            accountName={row.Account?.name}
            chargeId={row._id}
            defaultEmail={row.Account?.defaultEmails || []}
            copyPaymentLink={copyPaymentLink}
            documentType="paymentLink"
          />
        </Dialog>
      )}
    </>
  );
};

const EXPORT_COLUMNS = [
  { id: "Type", displayName: "Type" },
  { id: "Status", displayName: "Status" },
  { id: "Account Name", displayName: "Account Name" },
  { id: "Frequency", displayName: "Frequency" },
  { id: "Charged", displayName: "Charged" },
  { id: "Paid", displayName: "Paid" },
  { id: "Due", displayName: "Due" },
  { id: "Cycles", displayName: "Cycles" },
  { id: "Created Date", displayName: "Created Date" },
  { id: "Next Payment Date", displayName: "Next Payment Date" },
  { id: "Next Payment Amount", displayName: "Next Payment Amount" },
];

const PaymentPlans = ({ filter = {} }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const rowsPerPage = 30;
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [checked, setChecked] = useState([]);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const tableContainerRef = useRef(null);

  const buildBody = useCallback(() => {
    const body = {};
    if (filter.search) body.search = filter.search;
    const f = {};
    if (filter.type?.length) f.type = filter.type;
    if (filter.status?.length) f.status = filter.status;
    if (filter.accountId) f.accountId = filter.accountId;
    if (filter.createdAtRange) f.createdAtRange = filter.createdAtRange;
    if (filter.paymentDateRange) f.paymentDateRange = filter.paymentDateRange;
    if (filter.nextPaymentDateRange) f.nextPaymentDateRange = filter.nextPaymentDateRange;
    if (Object.keys(f).length) body.filter = f;
    if (sortField) body.sort = { [sortField]: sortDir === "asc" ? 1 : -1 };
    return body;
  }, [filter, sortField, sortDir]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await FinanceService.getAllRecurring(token, page + 1, rowsPerPage, buildBody());
      setData(res?.payload?.data || []);
      setCount(res?.payload?.pagination?.count || 0);
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Failed to load payment plans");
    } finally {
      setLoading(false);
    }
  }, [page, buildBody, getAccessTokenSilently]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); setChecked([]); }, [filter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedData = data;

  const onCheckboxChange = (e, row) => {
    setChecked((prev) =>
      prev.includes(row._id) ? prev.filter((id) => id !== row._id) : [...prev, row._id]
    );
  };

  const allChecked = data.length > 0 && data.every((r) => checked.includes(r._id));
  const onSelectAll = () => setChecked(allChecked ? [] : data.map((r) => r._id));

  const checkedRows = data.filter((r) => checked.includes(r._id));
  const totalCharged = checkedRows.reduce((sum, r) => sum + (+r.amount || 0), 0);
  const totalPaid = checkedRows.reduce((sum, r) => sum + (+r.paid || 0), 0);
  const totalDue = checkedRows.reduce((sum, r) => sum + ((+r.amount || 0) - (+r.paid || 0)), 0);
  const totalNextPayment = checkedRows.reduce((sum, r) => sum + (+r.nextPayment || 0), 0);

  const colSpan = COLUMNS.length + 4; // checkbox + expand + recurring icon + action

  const fetchExportData = async () => {
    setIsExportLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await FinanceService.exportAllRecurring(token, buildBody());
      const rows =
        res?.records ||
        res?.payload?.records ||
        res?.data?.records ||
        res?.payload?.data ||
        res?.data?.data ||
        res?.data ||
        [];
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Export failed");
      return [];
    } finally {
      setIsExportLoading(false);
    }
  };

  const mapExportRow = (row) => ({
    Type: getChargeTypeLabel(row.type),
    Status: getPaymentStatus(row),
    "Account Name": row.Account?.name || "",
    Frequency: row.frequency || "",
    Charged: row.amount || 0,
    Paid: row.paid || 0,
    Due: (row.amount || 0) - (row.paid || 0),
    Cycles: row.cycles || "",
    "Created Date": row.createdAt ? formatDate({ date: row.createdAt, formatType: "default" }) : "",
    "Next Payment Date": row.nextDate ? formatDate({ date: row.nextDate, formatType: "default" }) : "",
    "Next Payment Amount": row.nextPayment ?? "",
  });

  const generateCsv = async () => {
    const rows = await fetchExportData();
    return rows.map(mapExportRow);
  };

  const downloadExcel = async () => {
    setIsExportLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await FinanceService.exportAllRecurring(token, buildBody());
      const rows =
        res?.records ||
        res?.payload?.records ||
        res?.data?.records ||
        res?.payload?.data ||
        res?.data?.data ||
        res?.data ||
        [];
      const exportData = (Array.isArray(rows) ? rows : []).map(mapExportRow);
      const ws = XLSX.utils.json_to_sheet(sanitizeRows(exportData));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Payment Plans");
      XLSX.writeFile(wb, "payment-plans.xlsx");
    } catch (err) {
      toastr.error("Error", err?.data?.message || "Export failed");
    } finally {
      setIsExportLoading(false);
    }
  };

  return (
    <div className="w-100">
      <div className="family-table-container pl-7 pr-6 pt-3 pb-22">
      <div className="home-table-container mt-0 no-box-shadow">
          <div className="d-flex justify-content-between mb-5 h-40px" style={{ visibility: data.length > 0 && (filter.search || filter.type?.length || filter.status?.length || filter.accountId || filter.createdAtRange || filter.paymentDateRange || filter.nextPaymentDateRange) ? "visible" : "hidden" }}>
            <div />
            <div className="d-flex align-content-center" style={{ gap: "10px" }}>
              <ExportOptions
                  columns={EXPORT_COLUMNS}
                  datas={generateCsv}
                  downloadExcel={downloadExcel}
                  isLoading={isExportLoading}
                  setLoading={setIsExportLoading}
                  tableContainerRef={tableContainerRef}
                  reportFileNameHandler={() => "payment-plans"}
                  showPrint={false}
                />
            </div>
          </div>
          {checked.length > 0 && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "18px",
              fontSize: "14px",
              fontFamily: fontConfig.FinancePaymentPlansTextFont.family,
              color: "#4f4f4f",
              marginBottom: "10px",
              lineHeight: "1",
            }}>
              <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                {checked.length} <span style={{ fontWeight: 500 }}>selected</span>
              </span>
              <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontWeight: 500 }}>Charged:</span>
                {formatAmount(totalCharged)}
              </span>
              <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontWeight: 500 }}>Paid:</span>
                {formatAmount(totalPaid)}
              </span>
              <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontWeight: 500 }}>Due:</span>
                {formatAmount(totalDue)}
              </span>
              <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontWeight: 500 }}>Next Payment:</span>
                {formatAmount(totalNextPayment)}
              </span>
              <span
                onClick={() => setChecked([])}
                style={{ cursor: "pointer", color: "#2555EF", fontWeight: 500, fontSize: "14px", textDecoration: "underline", display: "inline-flex", alignItems: "center" }}
              >
                Clear
              </span>
            </div>
          )}
          <TableContainer component={Paper} className="scroll-table" ref={tableContainerRef} style={{ minHeight: loading ? "60vh" : undefined, overflowX: "auto" }}>
            <Table class="main-table charges-table" aria-label="payment plans table" style={{ minWidth: 1200, width: "100%", tableLayout: "auto" }}>
              <TableHead className="main-table__header sticky-table-header">
                <TableRow>
                  <TableCell padding="checkbox" className="main-width-class" />
                  <TableCell className="main-width-class" width="5%" />
                  <TableCell className="main-width-class" width="5%" />
                  {COLUMNS.map(({ label, field, minWidth, noSort }) => (
                    <TableCell key={field} style={{ fontWeight: 700, fontSize: 12, color: "#4d5565", minWidth }}>
                      {label}
                      {!noSort && (
                        <IconButton onClick={() => handleSort(field)}>
                          {sortField === field ? (
                            sortDir === "asc" ? <BsSortUp /> : <BsSortDown />
                          ) : (
                            <img src={sortImg} alt="sort" />
                          )}
                        </IconButton>
                      )}
                    </TableCell>
                  ))}
                  <TableCell className="empty-head sticky-header-column" />
                </TableRow>
              </TableHead>
              <TableBody style={{ backgroundColor: "white", display: "contents" }}>
                {data.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <div className="record-text">No record found</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((row, i) => (
                    <ExpandablePaymentRow
                      key={row._id ?? i}
                      row={row}
                      i={i}
                      checked={checked.includes(row._id)}
                      onCheckboxChange={onCheckboxChange}
                      onRefresh={fetchData}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {loading && <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}><Loader /></div>}

          {data.length > 0 && (
            <div className="pagination-custom mt-2">
              <Pagination
                count={Math.ceil(count / rowsPerPage)}
                color="primary"
                variant="outlined"
                page={page + 1}
                onChange={(_, value) => setPage(value - 1)}
              />
            </div>
          )}
      </div>
      </div>
    </div>
  );
};

export default PaymentPlans;
